// Builds the modules dropdown for AdminHeader. Filters by the user's
// own permissions module list so a buyer doesn't see invoice/payment
// links they can't actually use.
import { getModuleUrl, useTenant } from "@dehub/theme"
import {
  CoinIcon,
  CreditCardIcon,
  FileTextIcon,
  HandshakeIcon,
  MoneyWavyIcon,
  NetworkIcon,
  PackageIcon,
  PlugsConnectedIcon,
  ReceiptIcon,
  WarehouseIcon,
} from "@phosphor-icons/react"
import { useMemo } from "react"
import { TFunction } from "i18next"

interface ModuleLink {
  id: string
  label: string
  icon: typeof NetworkIcon
  href: string
  active: boolean
  badge?: number
}

const HIDE_FOR_BUYER = new Set(["price", "consignment", "inventory"])

export function useModuleLinks({
  t,
  accessToken,
  ownedModules,
  isBuyer,
  badges,
}: {
  t: TFunction
  accessToken: string | null | undefined
  ownedModules: string[]
  isBuyer: boolean
  badges?: Record<string, number>
}): ModuleLink[] {
  const tenant = useTenant()
  const modulesDomain = tenant?.modulesDomain

  return useMemo(() => {
    const m = (mod: string) => getModuleUrl(mod, modulesDomain)
    const tokenSuffix = `?token=${accessToken ?? ""}`
    const all = [
      { id: "network",     label: t("header.networkModule"),     icon: NetworkIcon,     href: `${m("network")}/access${tokenSuffix}`,      active: false, enabled: ownedModules.includes("NETWORK") },
      { id: "invoice",     label: t("header.invoiceModule"),     icon: ReceiptIcon,     href: `${m("invoice")}/access${tokenSuffix}`,      active: false, enabled: ownedModules.includes("INVOICE") },
      { id: "inventory",   label: t("header.inventoryModule"),   icon: WarehouseIcon,   href: `${m("item")}/access${tokenSuffix}`,         active: false, enabled: ownedModules.includes("INVENTORY") },
      { id: "payment",     label: t("header.paymentModule"),     icon: CreditCardIcon,  href: `${m("payment")}/access${tokenSuffix}`,      active: false, enabled: ownedModules.includes("PAYMENT") },
      { id: "order",       label: t("header.orderModule"),       icon: PackageIcon,     href: `${m("order")}/access${tokenSuffix}`,        active: true,  enabled: ownedModules.includes("ORDER") },
      { id: "contract",    label: t("header.contractModule"),    icon: FileTextIcon,    href: `${m("contract")}/access${tokenSuffix}`,     active: false, enabled: ownedModules.includes("CONTRACT") },
      { id: "price",       label: t("header.priceModule", { defaultValue: "Үнийн модуль" }), icon: CoinIcon,        href: `${m("price")}/access${tokenSuffix}`,        active: false, enabled: ownedModules.includes("PRICING") },
      { id: "finance",     label: t("header.financeModule"),     icon: MoneyWavyIcon,   href: `${m("")}?verifyConfirmType=modal`,          active: false, enabled: ownedModules.includes("FINANCE") },
      { id: "consignment", label: t("header.consignmentModule", { defaultValue: "Байршуулалтын модуль" }), icon: HandshakeIcon, href: `${m("consignment")}/access${tokenSuffix}`, active: false, enabled: ownedModules.includes("CONSIGNMENT") },
      { id: "erp",         label: t("header.erpModule"),         icon: PlugsConnectedIcon, href: `${m("erp")}/access${tokenSuffix}`,          active: false, enabled: ownedModules.includes("ERP") },
    ]
    const BADGE_KEY: Record<string, string> = { network: "NETWORK", order: "ORDER", invoice: "INVOICE", contract: "CONTRACT", price: "PRICING" }
    return all
      .filter((mod) => mod.enabled)
      .filter((mod) => !(isBuyer && HIDE_FOR_BUYER.has(mod.id)))
      .map(({ enabled: _enabled, ...rest }) => ({ ...rest, badge: badges?.[BADGE_KEY[rest.id] ?? ""] }))
  }, [t, accessToken, ownedModules, modulesDomain, isBuyer, badges])
}
